/**
 * Custom hook for managing all user bookmarks across all books
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '../config/api';
import { Bookmark, BookmarkUpdate } from './useBookmarks';

export interface BookInfo {
  id: string;
  title: string;
  author: string;
  cover_url: string | null;
  cover_presigned_url: string | null;
  total_pages: number | null;
}

export interface BookmarkWithBook extends Bookmark {
  book: BookInfo;
}

export interface GroupedBookmarks {
  book: BookInfo;
  bookmarks: BookmarkWithBook[];
}

export function useAllBookmarks() {
  const [bookmarks, setBookmarks] = useState<BookmarkWithBook[]>([]);
  const [groupedBookmarks, setGroupedBookmarks] = useState<GroupedBookmarks[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Group bookmarks by book
  const groupByBook = useCallback((bookmarksList: BookmarkWithBook[]): GroupedBookmarks[] => {
    const grouped = new Map<string, GroupedBookmarks>();

    bookmarksList.forEach(bookmark => {
      const bookId = bookmark.book.id;
      if (!grouped.has(bookId)) {
        grouped.set(bookId, {
          book: bookmark.book,
          bookmarks: []
        });
      }
      grouped.get(bookId)!.bookmarks.push(bookmark);
    });

    return Array.from(grouped.values());
  }, []);

  // Fetch all bookmarks
  const fetchAllBookmarks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/bookmarks');
      const data: BookmarkWithBook[] = response.data.bookmarks;
      setBookmarks(data);
      setGroupedBookmarks(groupByBook(data));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch bookmarks');
    } finally {
      setLoading(false);
    }
  }, [groupByBook]);

  // Update a bookmark
  const updateBookmark = useCallback(async (bookId: string, bookmarkId: string, data: BookmarkUpdate) => {
    try {
      const response = await api.patch(`/api/books/${bookId}/bookmarks/${bookmarkId}`, data);
      setBookmarks(prev => {
        const updated = prev.map(b => (b.id === bookmarkId ? { ...b, ...response.data } : b));
        setGroupedBookmarks(groupByBook(updated));
        return updated;
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to update bookmark');
    }
  }, [groupByBook]);

  // Delete a bookmark
  const deleteBookmark = useCallback(async (bookId: string, bookmarkId: string) => {
    try {
      await api.delete(`/api/books/${bookId}/bookmarks/${bookmarkId}`);
      setBookmarks(prev => {
        const filtered = prev.filter(b => b.id !== bookmarkId);
        setGroupedBookmarks(groupByBook(filtered));
        return filtered;
      });
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to delete bookmark');
    }
  }, [groupByBook]);

  // Load bookmarks on mount
  useEffect(() => {
    fetchAllBookmarks();
  }, [fetchAllBookmarks]);

  return {
    bookmarks,
    groupedBookmarks,
    loading,
    error,
    fetchAllBookmarks,
    updateBookmark,
    deleteBookmark,
    totalCount: bookmarks.length
  };
}
